import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Stack,
    Chip,
    Button,
    CircularProgress,
    Alert,
    alpha,
    useTheme,
    Divider,
    IconButton,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Fade,
} from "@mui/material";
import {
    Cake as CakeIcon,
    Celebration as AnniversaryIcon,
    PersonAdd as JoinerIcon,
    Send as SendIcon,
    EmojiEvents as TrophyIcon,
    Poll as PollIcon,
    Article as PostIcon,
    Favorite as HeartIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    FilterList as FilterIcon,
    ChatBubbleOutline as CommentIcon,
    Search as SearchIcon,
    MoreVert as MoreIcon,
    WorkspacePremium as PremiumIcon,
    CheckCircle as CheckCircleIcon
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const Engage = ({ activeTab: propActiveTab }) => {
    const theme = useTheme();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [feed, setFeed] = useState([]);
    const [filter, setFilter] = useState("ALL"); // ALL, KUDOS, POLL, POST

    // Determine active tab from prop or URL
    const [activeTab, setActiveTab] = useState(propActiveTab || (location.pathname.includes('feed') ? 'feed' : 'milestones'));

    useEffect(() => {
        if (propActiveTab) {
            setActiveTab(propActiveTab);
        } else {
            setActiveTab(location.pathname.includes('feed') ? 'feed' : 'milestones');
        }
    }, [propActiveTab, location.pathname]);
    const [events, setEvents] = useState({
        birthdays: [],
        workAnniversaries: [],
        weddingAnniversaries: [],
        newJoiners: [],
    });
    const [employees, setEmployees] = useState([]);
    const [openModal, setOpenModal] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    // Form States
    const [kudosData, setKudosData] = useState({ target_id: "", content: "", badge: "Team Player" });
    const [pollData, setPollData] = useState({ question: "", options: ["", ""] });
    const [postContent, setPostContent] = useState("");

    const badgeOptions = ["Team Player", "Innovator", "Hero", "Great Job", "Approachable", "Articulate", "Efficient", "Leader"];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            if (!token) {
                setError("Please log in again.");
                return;
            }
            const payload = JSON.parse(atob(token.split(".")[1]));
            const company_id = payload.company_id;

            const headers = { Authorization: `Bearer ${token}` };

            const [eventsRes, feedRes, employeesRes] = await Promise.all([
                axios.get(`${API_URL}/api/engage/upcoming-events`, { headers,  withCredentials: true,
 }),
                axios.get(`${API_URL}/api/engage/feed`, { headers,  withCredentials: true,
 }),
                axios.get(`${API_URL}/api/viewpersonal?limit=1000`, { headers,  withCredentials: true,
 })
            ]);

            setEvents({
                birthdays: Array.isArray(eventsRes.data?.birthdays) ? eventsRes.data.birthdays : [],
                workAnniversaries: Array.isArray(eventsRes.data?.workAnniversaries) ? eventsRes.data.workAnniversaries : [],
                weddingAnniversaries: Array.isArray(eventsRes.data?.weddingAnniversaries) ? eventsRes.data.weddingAnniversaries : [],
                newJoiners: Array.isArray(eventsRes.data?.newJoiners) ? eventsRes.data.newJoiners : [],
            });
            setFeed(feedRes.data.data || []);
            setEmployees(employeesRes.data.data || []);
        } catch (err) {
            //console.error("Error fetching data:", err);
            setError("Failed to load engagement data.");
        } finally {
            setLoading(false);
        }
    };

    const handleReaction = async (activityId) => {
        try {
            const token = localStorage.getItem("token");
            const payload = JSON.parse(atob(token.split(".")[1]));
            const employeeId = payload.employee_id;
            const headers = { Authorization: `Bearer ${token}` };

            const res = await axios.post(`${API_URL}/api/engage/activities/${activityId}/react`,
                { employeeId, reactionType: 'HEART' },
                { headers ,  withCredentials: true,
}
            );

            if (res.data.status === "success") {
                setFeed(prev => prev.map(item =>
                    item.id === activityId ? { ...item, reactions: res.data.reactions } : item
                ));
            }
        } catch (err) {
            //console.error("Error reacting:", err);
        }
    };

    const handlePollVote = async (activityId, optionIndex) => {
        try {
            const token = localStorage.getItem("token");
            const payload = JSON.parse(atob(token.split(".")[1]));
            const employeeId = payload.employee_id;
            const headers = { Authorization: `Bearer ${token}` };

            const res = await axios.post(
                `${API_URL}/api/engage/activities/${activityId}/vote`,
                { employeeId, optionIndex },
                { headers,  withCredentials: true,
 }
            );

            if (res.data.status === "success") {
                setFeed(prev => prev.map(item =>
                    item.id === activityId ? { ...item, metadata: res.data.metadata } : item
                ));
            }
        } catch (err) {
            //console.error("Error voting in poll:", err);
        }
    };

    const handleComment = async (activityId, text) => {
        if (!text.trim()) return;
        try {
            const token = localStorage.getItem('token');
            const payload = JSON.parse(atob(token.split(".")[1]));
            const employeeId = payload.employee_id;

            const res = await axios.post(`${API_URL}/api/engage/activities/${activityId}/comment`, {
                employeeId,
                text
            }, { headers: { Authorization: `Bearer ${token}` } ,  withCredentials: true,
});

            if (res.data.status === "success") {
                setFeed(prev => prev.map(a =>
                    a.id === activityId
                        ? { ...a, comments: [...(a.comments || []), res.data.comment] }
                        : a
                ));
            }
        } catch (error) {
            //console.error("Error adding comment:", error);
        }
    };

    const handleCreateActivity = async (type) => {
        try {
            setFormLoading(true);
            const token = localStorage.getItem("token");
            const payload = JSON.parse(atob(token.split(".")[1]));
            const company_id = payload.company_id;
            const sender_id = payload.employee_id;
            const headers = { Authorization: `Bearer ${token}`, };

            let body = { type, sender_id, company_id };

            if (type === 'KUDOS') {
                body.target_id = kudosData.target_id;
                body.content = kudosData.content;
                body.metadata = { badge: kudosData.badge };
            } else if (type === 'POLL') {
                body.content = pollData.question;
                body.metadata = { options: pollData.options.filter(o => o.trim() !== "") };
            } else if (type === 'POST') {
                body.content = postContent;
            }

            await axios.post(`${API_URL}/api/engage/activities`, body, { headers,  withCredentials: true,
 });
            setOpenModal(null);
            setKudosData({ target_id: "", content: "", badge: "Team Player" });
            setPollData({ question: "", options: ["", ""] });
            setPostContent("");
            fetchData();
        } catch (err) {
            //console.error("Error creating activity:", err);
        } finally {
            setFormLoading(false);
        }
    };

    const filteredFeed = filter === "ALL" ? feed : feed.filter(f => f.type === filter);

    const SidebarFilter = () => (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 5, border: '1px solid', borderColor: 'divider', bgcolor: 'transparent' }}>
            <Typography variant="overline" color="text.secondary" fontWeight={800} sx={{ px: 1, mb: 1, display: 'block' }}>Filters</Typography>
            <List sx={{ pt: 0 }}>
                {["ALL", "KUDOS", "POLL", "POST"].map((item) => {
                    const Icon = item === "ALL" ? FilterIcon : (item === "KUDOS" ? TrophyIcon : (item === "POLL" ? PollIcon : PostIcon));
                    return (
                        <ListItem key={item} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                onClick={() => setFilter(item)}
                                selected={filter === item}
                                sx={{
                                    borderRadius: 3,
                                    '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', '& .MuiListItemIcon-root': { color: 'primary.main' } },
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}><Icon fontSize="small" /></ListItemIcon>
                                <ListItemText primary={item === "KUDOS" ? "Recognition" : item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
                                {filter === item && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Paper>
    );

    const KudosCard = ({ item }) => {
        const isSelf = false; // logic for self kudos if needed
        return (
            <Card sx={{
                mb: 4, borderRadius: 6, overflow: 'visible', position: 'relative',
                border: '1px solid', borderColor: alpha(theme.palette.secondary.main, 0.1),
                boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
                '&::before': {
                    content: '""', position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                    width: 40, height: 40, background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23FFD700' d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2z'/%3E%3C/svg%3E") no-repeat center`,
                    backgroundSize: 'contain', zIndex: 1
                }
            }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Stack alignItems="center" spacing={2} mb={3}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={item.target?.personalPhoto ? (item.target.personalPhoto.startsWith('http') ? item.target.personalPhoto : `${API_URL}/uploads/${item.target.personalPhoto}`) : undefined}
                                sx={{
                                    width: 100, height: 100, fontSize: '2.5rem', fontWeight: 800,
                                    background: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                    border: '4px solid white', mb: 1
                                }}
                            >
                                {item.target?.firstName?.[0] || "U"}
                            </Avatar>
                            {/* Confetti Animation Placeholder UI */}
                            <Box sx={{
                                position: 'absolute', top: -20, left: -20, right: -20, bottom: -20,
                                border: '2px dashed #FFD700', borderRadius: '50%', opacity: 0.2, animation: 'spin 20s linear infinite'
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={900}>{item.target?.firstName} {item.target?.lastName}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar
                                    src={item.sender?.personalPhoto ? (item.sender.personalPhoto.startsWith('http') ? item.sender.personalPhoto : `${API_URL}/uploads/${item.sender.personalPhoto}`) : undefined}
                                    sx={{ width: 24, height: 24, fontSize: '0.65rem' }}
                                >
                                    {item.sender?.firstName?.[0]}
                                </Avatar>
                                <Typography variant="subtitle2" color="text.secondary">Recognized by {item.sender?.firstName}</Typography>
                            </Stack>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 3 }}>
                        <Chip
                            label={item.metadata?.badge || "Hero"}
                            icon={<PremiumIcon sx={{ fontSize: '1rem !important' }} />}
                            sx={{
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                color: 'secondary.main', fontWeight: 800, px: 2, height: 32,
                                border: '1px solid', borderColor: alpha(theme.palette.secondary.main, 0.2)
                            }}
                        />
                    </Stack>

                    <Paper elevation={0} sx={{ p: 3, bgcolor: '#fdfdfd', borderRadius: 4, border: '1px solid #f0f0f0', fontStyle: 'italic', color: 'text.secondary', position: 'relative' }}>
                        <Typography variant="body1">"{item.content}"</Typography>
                    </Paper>

                    <Divider sx={{ my: 3 }} />

                    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                onClick={() => handleReaction(item.id)}
                                startIcon={<HeartIcon sx={{ color: item.reactions?.length > 0 ? '#ff4081' : 'inherit' }} />}
                                sx={{ borderRadius: 4, fontWeight: 700 }}
                            >
                                {item.reactions?.length || 0}
                            </Button>
                            <Button size="small" startIcon={<CommentIcon />} sx={{ borderRadius: 4, fontWeight: 700 }}>{item.comments?.length || 0}</Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{new Date(item.createdAt).toLocaleDateString()}</Typography>
                    </Stack>

                    {/* Comments List */}
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Stack spacing={2}>
                            {(item.comments || []).map((comment) => (
                                <Stack key={comment.id} direction="row" spacing={1.5} alignItems="flex-start">
                                    <Avatar
                                        src={comment.employee?.personalPhoto ? (comment.employee.personalPhoto.startsWith('http') ? comment.employee.personalPhoto : `${API_URL}/uploads/${comment.employee.personalPhoto}`) : undefined}
                                        sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                                    >
                                        {comment.employee?.firstName?.[0] || "U"}
                                    </Avatar>
                                    <Box sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 3, flexGrow: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={800} fontSize="0.8rem">
                                            {comment.employee?.firstName} {comment.employee?.lastName}
                                        </Typography>
                                        <Typography variant="body2" fontSize="0.85rem">
                                            {comment.text}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>

                        <CommentInput activityId={item.id} />
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const StandardCard = ({ item }) => {
        const token = localStorage.getItem("token");
        let currentEmployeeId = null;

        if (token) {
            try {
                currentEmployeeId = JSON.parse(atob(token.split(".")[1])).employee_id;
            } catch (err) {
                currentEmployeeId = null;
            }
        }

        return (
        <Card sx={{ mb: 4, borderRadius: 5, border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }}>
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                    <Avatar
                        src={item.sender?.personalPhoto ? (item.sender.personalPhoto.startsWith('http') ? item.sender.personalPhoto : `${API_URL}/uploads/${item.sender.personalPhoto}`) : undefined}
                        sx={{ width: 44, height: 44 }}
                    >
                        {item.sender?.firstName?.[0]}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{item.sender?.firstName} {item.sender?.lastName}</Typography>
                        <Typography variant="caption" color="text.secondary">{new Date(item.createdAt).toLocaleDateString()}</Typography>
                    </Box>
                    {/* <IconButton size="small"><MoreIcon /></IconButton> */}
                </Stack>

                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>{item.content}</Typography>

                {item.type === 'POLL' && item.metadata?.options && (
                    <Stack spacing={1.5} mb={3}>
                        {item.metadata.options.map((opt, idx) => {
                            const votes = Array.isArray(item.metadata?.votes) ? item.metadata.votes : [];
                            const selectedVote = votes.find(v => String(v.employeeId) === String(currentEmployeeId));
                            const optionVotes = votes.filter(v => Number(v.optionIndex) === idx).length;
                            const totalVotes = votes.length;
                            const percentage = totalVotes ? Math.round((optionVotes / totalVotes) * 100) : 0;
                            const isSelected = Number(selectedVote?.optionIndex) === idx;

                            return (
                                <Button
                                    key={idx}
                                    variant={isSelected ? "contained" : "outlined"}
                                    fullWidth
                                    onClick={() => handlePollVote(item.id, idx)}
                                    endIcon={isSelected ? <CheckCircleIcon /> : null}
                                    sx={{
                                        justifyContent: 'space-between', borderRadius: 3, py: 1.5, px: 3,
                                        textTransform: 'none',
                                        borderColor: isSelected ? 'primary.main' : '#eee',
                                        color: isSelected ? 'primary.contrastText' : 'text.primary',
                                        bgcolor: isSelected ? 'primary.main' : 'transparent',
                                        '&:hover': {
                                            bgcolor: isSelected ? 'primary.dark' : alpha(theme.palette.primary.main, 0.05),
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                >
                                    <Box component="span" sx={{ textAlign: 'left', fontWeight: isSelected ? 800 : 600 }}>
                                        {opt}
                                    </Box>
                                    <Box component="span" sx={{ ml: 2, fontSize: '0.8rem', opacity: 0.85 }}>
                                        {totalVotes > 0 ? `${percentage}% (${optionVotes})` : ''}
                                    </Box>
                                </Button>
                            );
                        })}
                    </Stack>
                )}

                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={2}>
                    <Button
                        size="small"
                        onClick={() => handleReaction(item.id)}
                        startIcon={<HeartIcon sx={{ color: item.reactions?.length > 0 ? '#ff4081' : 'inherit' }} />}
                        sx={{ borderRadius: 4, fontWeight: 700 }}
                    >
                        {item.reactions?.length || 0}
                    </Button>
                    <Button size="small" startIcon={<CommentIcon />} sx={{ borderRadius: 4, fontWeight: 700 }}>{item.comments?.length || 0}</Button>
                </Stack>

                {/* Comments List */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Stack spacing={2}>
                        {(item.comments || []).map((comment) => (
                            <Stack key={comment.id} direction="row" spacing={1.5} alignItems="flex-start">
                                <Avatar
                                    src={comment.employee?.personalPhoto ? (comment.employee.personalPhoto.startsWith('http') ? comment.employee.personalPhoto : `${API_URL}/uploads/${comment.employee.personalPhoto}`) : undefined}
                                    sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                                >
                                    {comment.employee?.firstName?.[0] || "U"}
                                </Avatar>
                                <Box sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 3, flexGrow: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={800} fontSize="0.8rem">
                                        {comment.employee?.firstName} {comment.employee?.lastName}
                                    </Typography>
                                    <Typography variant="body2" fontSize="0.85rem">
                                        {comment.text}
                                    </Typography>
                                </Box>
                            </Stack>
                        ))}
                    </Stack>

                    <CommentInput activityId={item.id} />
                </Box>
            </CardContent>
        </Card>
        );
    };

    const CommentInput = ({ activityId }) => {
        const [text, setText] = useState("");
        const token = localStorage.getItem("token");
        const payload = token ? JSON.parse(atob(token.split(".")[1])) : null;
        const currentUser = employees.find(e => e.employee_id === payload?.employee_id);

        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <Avatar
                    src={currentUser?.personalPhoto ? (currentUser.personalPhoto.startsWith('http') ? currentUser.personalPhoto : `${API_URL}/uploads/${currentUser.personalPhoto}`) : undefined}
                    sx={{ width: 32, height: 32 }}
                >
                    {currentUser?.firstName?.[0] || "U"}
                </Avatar>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Write a comment..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 4, bgcolor: 'white' }
                    }}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleComment(activityId, text);
                            setText("");
                        }
                    }}
                />
                <IconButton
                    color="primary"
                    onClick={() => {
                        handleComment(activityId, text);
                        setText("");
                    }}
                    disabled={!text.trim()}
                >
                    <SendIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
            </Stack>
        );
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}><CircularProgress size={40} /></Box>;

    // Days remaining until the next occurrence of a month/day, relative to today.
    // Returns null when the source date is missing or invalid.
    const daysUntilNextOccurrence = (dateString) => {
        if (!dateString) return null;
        const source = new Date(dateString);
        if (isNaN(source.getTime())) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let next = new Date(today.getFullYear(), source.getMonth(), source.getDate());
        next.setHours(0, 0, 0, 0);
        if (next < today) {
            next = new Date(today.getFullYear() + 1, source.getMonth(), source.getDate());
        }
        return Math.round((next - today) / (1000 * 60 * 60 * 24));
    };

    const daysSince = (dateString) => {
        if (!dateString) return null;
        const source = new Date(dateString);
        if (isNaN(source.getTime())) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        source.setHours(0, 0, 0, 0);
        return Math.round((today - source) / (1000 * 60 * 60 * 24));
    };

    const relativeDayLabel = (days) => {
        if (days === null || days === undefined) return null;
        if (days === 0) return "Today";
        if (days === 1) return "Tomorrow";
        return `In ${days} days`;
    };

    const CelebrationsView = () => {
        const categories = [
            {
                title: "Upcoming Birthdays",
                icon: <CakeIcon fontSize="small" />,
                tone: theme.palette.error,
                data: Array.isArray(events?.birthdays) ? events.birthdays : [],
                type: 'BIRTHDAY',
                emptyMsg: "No birthdays coming up",
            },
            {
                title: "Work Anniversaries",
                icon: <AnniversaryIcon fontSize="small" />,
                tone: theme.palette.warning,
                data: Array.isArray(events?.workAnniversaries) ? events.workAnniversaries : [],
                type: 'WORK',
                emptyMsg: "No work anniversaries coming up",
            },
            {
                title: "New Joiners",
                icon: <JoinerIcon fontSize="small" />,
                tone: theme.palette.success,
                data: Array.isArray(events?.newJoiners) ? events.newJoiners : [],
                type: 'JOINER',
                emptyMsg: "No new team members recently",
            },
            {
                title: "Wedding Anniversaries",
                icon: <AnniversaryIcon fontSize="small" />,
                tone: theme.palette.info,
                data: Array.isArray(events?.weddingAnniversaries) ? events.weddingAnniversaries : [],
                type: 'WEDDING',
                emptyMsg: "No wedding anniversaries coming up",
            },
        ];

        const totalMilestones = categories.reduce((sum, cat) => sum + (cat.data?.length || 0), 0);

        return (
            <Box sx={{ width: '100%', pb: 8 }}>
                {/* Header */}
                <Box
                    sx={{
                        mb: 4,
                        pb: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h4" fontWeight='bold'  gutterBottom>
                            Milestones
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
                            A unified view of upcoming birthdays, anniversaries, and new team members.
                        </Typography>
                    </Box>

                  
                </Box>

                {totalMilestones === 0 ? (
                    <Box
                        sx={{
                            py: 10,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: alpha(theme.palette.text.primary, 0.02),
                        }}
                    >
                        <Typography variant="body1" color="text.secondary" fontWeight={600}>
                            Nothing on the calendar right now
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                            New milestones will appear here automatically as they come up.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {categories.map((cat, idx) => (
                            <Grid item xs={12} lg={6} key={idx}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        overflow: 'hidden',
                                        height: '100%',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{
                                            px: 3,
                                            py: 2,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: alpha(cat.tone.main, 0.04),
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    bgcolor: alpha(cat.tone.main, 0.14),
                                                    color: cat.tone.dark || cat.tone.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {cat.icon}
                                            </Box>
                                            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                                                {cat.title}
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            label={cat.data.length}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                bgcolor: alpha(cat.tone.main, 0.12),
                                                color: cat.tone.dark || cat.tone.main,
                                            }}
                                        />
                                    </Stack>

                                    <Box sx={{ p: cat.data.length ? 1 : 0 }}>
                                        {cat.data.length === 0 ? (
                                            <Box sx={{ py: 5, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.disabled" fontWeight={500}>
                                                    {cat.emptyMsg}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <List disablePadding>
                                                {cat.data.map((emp, empIdx) => {
                                                    let dateLabel = null;
                                                    let relLabel = null;

                                                    if (cat.type === 'BIRTHDAY') {
                                                        dateLabel = new Date(emp.dateOfBirth).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                                        relLabel = relativeDayLabel(daysUntilNextOccurrence(emp.dateOfBirth));
                                                    } else if (cat.type === 'WORK') {
                                                        dateLabel = `${emp.years} yr${Number(emp.years) === 1 ? '' : 's'} • ${new Date(emp.joiningDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                                                        relLabel = relativeDayLabel(daysUntilNextOccurrence(emp.joiningDate));
                                                    } else if (cat.type === 'JOINER') {
                                                        dateLabel = `Joined ${new Date(emp.joiningDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                                                        const since = daysSince(emp.joiningDate);
                                                        relLabel = since !== null ? (since === 0 ? "Today" : `${since} day${since === 1 ? '' : 's'} ago`) : null;
                                                    } else {
                                                        dateLabel = `${emp.years} yr${Number(emp.years) === 1 ? '' : 's'} together`;
                                                    }

                                                    return (
                                                        <ListItem
                                                            key={emp.employee_id}
                                                            disablePadding
                                                            sx={{
                                                                borderTop: empIdx === 0 ? 'none' : '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 2,
                                                                    px: 2,
                                                                    py: 1.5,
                                                                    borderRadius: 2,
                                                                    transition: 'background-color 0.15s ease',
                                                                    '&:hover': { bgcolor: alpha(cat.tone.main, 0.04) },
                                                                }}
                                                            >
                                                                <Avatar
                                                                    src={emp.photo ? (emp.photo.startsWith('http') ? emp.photo : `${API_URL}/uploads/${emp.photo}`) : undefined}
                                                                    sx={{
                                                                        width: 44,
                                                                        height: 44,
                                                                        bgcolor: alpha(cat.tone.main, 0.12),
                                                                        color: cat.tone.dark || cat.tone.main,
                                                                        fontWeight: 700,
                                                                        fontSize: '1rem',
                                                                    }}
                                                                >
                                                                    {emp.firstName?.[0]}
                                                                </Avatar>

                                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight={700} color="text.primary" noWrap>
                                                                        {emp.firstName} {emp.lastName}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" noWrap component="span" sx={{ display: 'block' }}>
                                                                        {dateLabel}
                                                                    </Typography>
                                                                </Box>

                                                                {relLabel && (
                                                                    <Chip
                                                                        label={relLabel}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            fontSize: '0.7rem',
                                                                            height: 24,
                                                                            borderColor: alpha(cat.tone.main, 0.3),
                                                                            color: cat.tone.dark || cat.tone.main,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="xl">
                <Box mb={4} sx={{ display: activeTab === 'milestones' ? 'none' : 'block' }}>
                    <Typography variant="h4" fontWeight={900} sx={{ color: 'text.primary', letterSpacing: '-0.02em', mb: 1 }}>
                        {activeTab === 'milestones' ? 'Milestones' : 'Social Hub'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {activeTab === 'milestones' ? 'Celebrate milestones and welcome new team members.' : 'Connect and share with your team across the organization.'}
                    </Typography>
                </Box>

                {activeTab === 'milestones' ? (
                    <CelebrationsView />
                ) : (
                    <Grid container spacing={4}>
                        {/* Left Column: Sidebar Filters */}
                        <Grid item xs={12} lg={3} sx={{ display: { xs: 'none', lg: 'block' } }}>
                            <Box sx={{ position: 'sticky', top: 24 }}>
                                <SidebarFilter />

                                <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 5, border: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.secondary.main, 0.02) }}>
                                    <Typography variant="subtitle2" fontWeight={800} gutterBottom>Engagement Stats</Typography>
                                    <Stack spacing={2} sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Total Recognition</Typography>
                                            <Typography variant="body2" fontWeight={700}>{feed.filter(f => f.type === 'KUDOS').length}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Active Polls</Typography>
                                            <Typography variant="body2" fontWeight={700}>{feed.filter(f => f.type === 'POLL').length}</Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Box>
                        </Grid>

                        {/* Middle Column: Feed Content */}
                        <Grid item xs={12} lg={9}>
                            {/* Feed Hero Header */}
                            {/* <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 4, md: 5 },
                                    mb: 4,
                                    borderRadius: 8,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                                    color: theme.palette.text.primary,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
                                }}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1 }}>
                                    
                                    <Typography variant="h3" fontWeight={900} sx={{ mb: 1, fontSize: { xs: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                        Connect & Collaborate
                                    </Typography>
                                    <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 500, fontWeight: 400 }}>
                                        Stay updated with the latest achievements, participate in polls, and share important updates with your team.
                                    </Typography>
                                </Box>
                                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 220, height: 220, borderRadius: '50%', background: alpha(theme.palette.secondary.light, 0.15), filter: 'blur(40px)' }} />
                            </Paper> */}

                            {/* Quick Action Bar */}
                            <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 5, border: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, }}>
                                <Button
                                    fullWidth
                                    startIcon={<TrophyIcon />}
                                    variant="outlined"

                                    onClick={() => setOpenModal('KUDOS')}
                                    sx={{ borderRadius: 4, py: 1.2, fontWeight: 800, fontSize: '0.9rem', boxShadow: 'none' }}
                                >
                                    Recognition
                                </Button>
                                <Button
                                    fullWidth
                                    startIcon={<PollIcon />}
                                    variant="outlined"
                                    onClick={() => setOpenModal('POLL')}
                                    sx={{ borderRadius: 4, py: 1.2, fontWeight: 800, fontSize: '0.9rem',  }}
                                >
                                    Quick Poll
                                </Button>
                                <Button
                                    fullWidth
                                    startIcon={<PostIcon />}
                                    variant="outlined"
                                    onClick={() => setOpenModal('POST')}
                                    sx={{ borderRadius: 4, py: 1.2, fontWeight: 800, fontSize: '0.9rem', }}
                                >
                                    New Post
                                </Button>
                            </Paper>

                            {/* Mobile Filters (visible only on mobile) */}
                            <Box sx={{ mb: 3, display: { xs: 'flex', lg: 'none' }, borderBottom: 1, borderColor: 'divider', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                {["ALL", "KUDOS", "POLL", "POST"].map((item) => (
                                    <Button
                                        key={item}
                                        onClick={() => setFilter(item)}
                                        sx={{
                                            px: 4, py: 1.5,
                                            borderRadius: 0,
                                            fontWeight: 800,
                                            flexShrink: 0,
                                            color: filter === item ? 'secondary.main' : 'text.secondary',
                                            borderBottom: filter === item ? '3px solid' : 'none',
                                            borderColor: 'secondary.main',
                                            '&:hover': { bgcolor: 'transparent', color: 'secondary.main' }
                                        }}
                                    >
                                        {item === "ALL" ? "Timeline" : (item === "KUDOS" ? "Kudos" : (item === "POLL" ? "Polls" : "Posts"))}
                                    </Button>
                                ))}
                            </Box>

                            {filteredFeed.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 12, bgcolor: 'white', borderRadius: 6, border: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ opacity: 0.2, mb: 2 }}><PostIcon sx={{ fontSize: 60 }} /></Box>
                                    <Typography variant="h6" color="text.secondary" fontWeight={700}>No activities yet</Typography>
                                    <Typography variant="body2" color="text.disabled">Be the first to share something with your team!</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {filteredFeed.map(item => item.type === 'KUDOS' ? <KudosCard key={item.id} item={item} /> : <StandardCard key={item.id} item={item} />)}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Container>

            {/* Creation Modals */}
            <Dialog open={openModal === 'KUDOS'} onClose={() => setOpenModal(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>Give Kudos</DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Who do you want to recognize?</InputLabel>
                            <Select
                                value={kudosData.target_id}
                                label="Who do you want to recognize?"
                                onChange={(e) => setKudosData({ ...kudosData, target_id: e.target.value })}
                            >
                                {employees.map(emp => (
                                    <MenuItem key={emp.employee_id} value={emp.employee_id}>
                                        {emp.firstName} {emp.lastName} ({emp.employee_id})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Select a Badge</InputLabel>
                            <Select
                                value={kudosData.badge}
                                label="Select a Badge"
                                onChange={(e) => setKudosData({ ...kudosData, badge: e.target.value })}
                            >
                                {badgeOptions.map(opt => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Your Message"
                            placeholder="What did they do that was awesome?"
                            value={kudosData.content}
                            onChange={(e) => setKudosData({ ...kudosData, content: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 4 }}>
                    <Button onClick={() => setOpenModal(null)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={formLoading || !kudosData.target_id || !kudosData.content}
                        onClick={() => handleCreateActivity('KUDOS')}
                        sx={{ py: 1, px: 4, borderRadius: 3 }}
                    >
                        Send Kudos
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Poll Modal */}
            <Dialog open={openModal === 'POLL'} onClose={() => setOpenModal(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>Create Quick Poll</DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="What's your question?"
                            value={pollData.question}
                            onChange={(e) => setPollData({ ...pollData, question: e.target.value })}
                        />
                        <Box>
                            <Typography variant="overline" color="text.secondary" fontWeight={800} mb={1} display="block">Options</Typography>
                            {pollData.options.map((opt, idx) => (
                                <Stack key={idx} direction="row" spacing={1} mb={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder={`Option ${idx + 1}`}
                                        value={opt}
                                        onChange={(e) => {
                                            const newOpts = [...pollData.options];
                                            newOpts[idx] = e.target.value;
                                            setPollData({ ...pollData, options: newOpts });
                                        }}
                                    />
                                    {pollData.options.length > 2 && (
                                        <IconButton size="small" onClick={() => setPollData({ ...pollData, options: pollData.options.filter((_, i) => i !== idx) })}>
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                            <Button startIcon={<AddIcon />} onClick={() => setPollData({ ...pollData, options: [...pollData.options, ""] })}>Add Option</Button>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 4 }}>
                    <Button onClick={() => setOpenModal(null)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={formLoading || !pollData.question || pollData.options.filter(o => o.trim() !== "").length < 2}
                        onClick={() => handleCreateActivity('POLL')}
                        sx={{ py: 1, px: 4, borderRadius: 3 }}
                    >
                        Launch Poll
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Post Modal */}
            <Dialog open={openModal === 'POST'} onClose={() => setOpenModal(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>Create Post</DialogTitle>
                <Divider />
                <DialogContent>
                    <TextField
                        sx={{ mt: 2 }}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="What's happening? Share some news or an announcement!"
                        value={postContent}
                        onChange={(e) => setPostContent(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 4 }}>
                    <Button onClick={() => setOpenModal(null)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={formLoading || !postContent}
                        onClick={() => handleCreateActivity('POST')}
                        sx={{ py: 1, px: 4, borderRadius: 3 }}
                    >
                        Share Post
                    </Button>
                </DialogActions>
            </Dialog>

            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
        </Box>
    );
};

export default Engage;